import React, { useState, useEffect } from 'react'
import messages from './messages'
import { useIntl } from 'react-intl'
import SupplierSelect from 'components/SupplierSelect'
import {
  Banner,
  Button,
  Input,
  Modal,
  Select,
  SelectAsync,
  SelectAsyncInline,
  TextArea,
  selectComponents,
  Icon,
} from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import debounce from 'debounce-promise'
import DateTime from "react-datetime"
import useCurrentUser from 'hooks/useCurrentUser'
import classNames from 'classnames'

export default NiceModal.create(({ task, order, version, modelFiles = [], reAssign = false }) => {
  const { formatMessage, formatDate, formatTime } = useIntl()
  const { name, roles = [] } = useCurrentUser()
  const modal = useModal()

  const [fileSpecOptions] = useApi(`/rest/v2/task_filespecs?search[hidden]=0&search[deleted]=0&search[ordertasktype_uid]=${task.type.uid || null}`)
  const [filesFromApi, isLoadingFiles] = useApi(`/rest/v1/task/${task.uid || null}/files`)

  const [files, setFiles] = useState([]);

  useEffect(() => {
    setFiles(filesFromApi);
  }, [filesFromApi]);

  const latestAssignedTask = resolveLatestAssignedTask(order)
  const [loading, setLoading] = useState(false)
  const [defaults, setDefaults] = useState({})
  const [supplier, setSupplier] = useState(task?.preferredSupplier || null)
  const [supplierTypeIds, setSupplierTypeIds] = useState([])

  const [deadline, setDeadline] = useState(() => {
    const timestamp = parseInt(latestAssignedTask?.deadline) || parseInt(order.deadline)
    const now = new Date()

    if (timestamp > 0) {
      const suggestedDate = new Date(timestamp * 1000)

      if (suggestedDate > now) {
        return suggestedDate
      }
    }

    return now
  })

  const defaultCosts = {
    PR: 15
  }

  const [rates, setRates] = useState({
    price: Number(task.price) || 0.0,
    cost: +task.cost
      ? Number(task.cost)
      : (task.type.shortname in defaultCosts
      ? Number(defaultCosts[task.type.shortname])
      : 0.0),
    priceCurrency: {
      label: task.pricecurrency || order.customer.currency.iso || 'EUR',
      value: task.pricecurrencyId || order.customer.currency.uid || 2,
    },
    costCurrency: {
      label: task.costcurrency || order.customer.currency.iso || 'EUR',
      value: task.costcurrencyId || order.customer.currency.uid || 2,
    },
    discount: Number(task.discount) || 0,
  })

  const [brief, setBrief] = useState(() => {
    if (task.externalnote.length > 1) {
      return task.externalnote
    }

    if (!['LSA', 'LSP', 'USAGE'].includes(task.type.shortname) && latestAssignedTask?.externalnote.length > 1) {
      return latestAssignedTask.externalnote
    }

    return (`briefTemplate${task.type.shortname}` in messages) 
      ? formatMessage(messages[`briefTemplate${task.type.shortname}`], { name })
      : ''
  })
  
  const [internalMsg, setInternalMsg] = useState(() => {
    if (task.internalnote.length > 1) {
      return task.internalnote
    }

    if (!['LSA', 'LSP', 'USAGE'].includes(task.type.shortname) && latestAssignedTask?.internalnote.length > 1) {
      return latestAssignedTask.internalnote
    }

    return ''
  })

  const [fileSpecs, setFileSpecs] = useState(() => {
    return latestAssignedTask?.fileSpecs || []
  })

  useEffect(() => {
    if (defaults?.fileSpecs && task.type.uid in defaults.fileSpecs) {
      setFileSpecs(defaults.fileSpecs[task.type.uid].map(({ id, name, }) => ({ value: id, label: name })))
    }
  }, [defaults])

  useEffect(() => {
    handleRateConversionForSupplier()
  }, [task, supplier])

  useEffect(() => {
    fetch(`/rest/v2/task/defaults`)
      .then((response) => response.json())
      .then(({ data }) => setDefaults(data))

    if (task.uid) {
      fetch(`/rest/v2/task/${task.uid}/supplier-types`)
        .then((response) => response.json())
        .then(({data, success}) => {
          if (success) {
            setSupplierTypeIds(data.supplierTypeIds)
          }
        })
        .catch((error) => window.alert(error))
    }
  }, [])

  const onSubmit = async () => {
    setLoading(true)

    if(
      (+rates?.cost <= 0 && !task.retakereason && !['AU', 'PR', 'NPR'].includes(task.type.shortname)) // Retakes + auditions + PP do not require cost
      || (+rates?.price <= 0 && !task.retakereason && !['AU', 'PR', 'NPR'].includes(task.type.shortname)) // Retakes + auditions + PP do not require price
    ) {
      if (!confirm('Cost or Prices is not set' + "\n\n" +'Are you sure this is what you want?')) {
        setLoading(false)
        return
      }
    }

    if (!await validateCostPriceRelation()) {
      setLoading(false);
      return
    }

    const body = new FormData()

    const local = new Date(deadline)
    local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
    const [newDate, newTime] = local.toJSON().slice(0, 19).split('T')

    Object.entries({
      internal: internalMsg,
      taskid: task.uid,
      supplierid: supplier?.uid || null,
      external: brief,
      cost: rates.cost,
      price: rates.price,
      discount: rates.discount,
      filespecs: fileSpecs?.map(({ value }) => value).join(',') || '',
      costCurrency: rates.costCurrency.value,
      priceCurrency: rates.priceCurrency.value,
      newDate,
      newTime,
    }).map(([key, value]) => body.append(key, value))

    fetch(`/ordertask/addsupplier`, { method: 'POST', body })
      .then((response) => response.text())
      .then((response) => {
        if (response !== 'ok') {
          throw 'Error assigning supplier'
        }

        return true
      })
      .catch((error) => window.alert(error))
      .finally(() => {
        modal.resolve({ task, order, assigned: true })
        modal.remove()
      })
  }

  const handleRateConversionForSupplier = async () => {
    // Auto exchange cost amount and currency, when supplier changes
    if (rates.cost > 0 && supplier?.currency?.iso && rates.costCurrency !== supplier.currency.iso) {
      const convertedAmount = await convertRate(rates.costCurrency.label, supplier.currency.iso, rates.cost)
      setRates((prev) => ({
        ...prev,
        cost: convertedAmount,
        costCurrency: {
          value: supplier.currency.uid,
          label: supplier.currency.iso
        },
      }))
    } else if (supplier?.currency?.iso && rates.costCurrency !== supplier.currency.iso) {
      setRates((prev) => ({
        ...prev,
        costCurrency: {
          value: supplier.currency.uid,
          label: supplier.currency.iso
        }
      }))
    }
  }

  const convertRate = async (fromCurrencyIso, toCurrencyIso, amount) => {
    const response = await fetch(`/rest/v2/currency/exchange`, {
      method: 'POST',
      body: JSON.stringify({
        from: fromCurrencyIso,
        to: toCurrencyIso,
        amount: amount,
      })
    })
    const jsonData = await response.json()

    return Math.round(jsonData.data.amount / 5) * 5
  }

  const validateCostPriceRelation = async () => {
    if (rates.priceCurrency.value == rates.costCurrency.value) {
      if (rates.price == rates.cost) {
        return true
      }
      return confirmCostLargerThanPrice(parseFloat(rates.cost), parseFloat(rates.price))
    } else if (rates.price == 0 && rates.cost == 0) {
      return true
    } else if (rates.cost == 0) {
      return true
    }

    const convertedAmount = await convertRate(rates.costCurrency.label, rates.priceCurrency.label, rates.cost)

    return confirmCostLargerThanPrice(parseFloat(convertedAmount), parseFloat(rates.price))
  }

  const confirmCostLargerThanPrice = (cost, price) => {
    if (cost > price) {
      if (confirm('Cost is greater than Price.' + "\n\n" +'Are you sure this is what you want?')) {
          return true;
      }
      return false;
    }
    return true;
  }

  const disabled = loading
    || !supplier?.uid
    || !brief?.length
    || (!fileSpecs?.length && !!fileSpecOptions?.length) 

  return (
    <Modal
      show={modal.visible}
      onClose={() => modal.remove()}
      onSubmit={() => disabled ? undefined : onSubmit()}
      title={reAssign ? formatMessage(messages.reAssignTitle) : formatMessage(messages.assignTitle)}
      dialogPanelClasses="max-w-2xl"
    >
      <div className="space-y-4">

      { supplier?.commentsinternal?.length &&
          <Banner
            state="info"
            title={supplier?.commentsinternal}
          />
        }

        { supplier?.uid && rates?.costCurrency?.label !== supplier?.currency?.iso &&
          <Banner
            state="error"
            title={`Cost currency(${rates?.costCurrency?.label}) is not the same as supplier currency(${supplier?.currency?.iso})`}
          />
        }

        { task.type.shortname == 'VO' && !files?.some(({ issource }) => Boolean(Number(issource))) && !isLoadingFiles &&
          <Banner
            state="warning"
            title="This voice over task has no manuscript uploaded!"
          />
        }

        { ['PR', 'NPR'].includes(task.type.shortname) && parseInt(order.word_count) == 0 &&
          <Banner
            state="warning"
            title="This order does not have a word count"
          />
        }

        <SupplierSelect
          key={`supplierSelect-${supplierTypeIds}`}
          required
          value={supplier ? { value: supplier.uid, label: supplier.userid } : null}
          onChange={(data) => setSupplier(data)}
          loadOptions={
            debounce((query) => {
              const params = new URLSearchParams({ pageSize: 500, includeRelations: 1 })

              if (version.targetlanguageId > 0 && !['PR', 'TR'].includes(task.type.shortname)) {
                params.append('search[language]', version.targetlanguageId)
                params.append('search[language-proficiency]', [1, 2, 3])
              }

              if (query.length > 0) {
                params.append('search[query]', query)
              }

              if (supplierTypeIds?.length > 0) {
                params.append('search[type]', supplierTypeIds)
              }

              params.append('search[status]', '2,4') // Active and Online

              return fetch(`/rest/v2/supplier?${params.toString()}`)
                .then((response) => response.json())
                .then((data) => data.map((supplier) => ({ value: supplier.uid, label: supplier.userid, data: supplier })))
            }, 250)
          }
        />

        <div className="flex justify-between items-center space-x-4">
          <div className="flex items-center w-5/12  space-x-2">
            <Input
              required
              size="sm"
              label="Cost"
              type="number"
              value={rates.cost}
              onChange={({ target: { value } }) => setRates((prev) => ({ ...prev, cost: value }))}
            />

            <SelectAsyncInline
              disabled={!roles.includes('finance')}
              className="mt-12"
              size="sm"
              value={rates.costCurrency}
              onChange={(value) => setRates((prev) => ({ ...prev, costCurrency: value }))}
              options={[]}
              cacheOptions
              defaultOptions
              loadOptions={
                debounce((query) => fetch(`/rest/v1/currency?search[iso]=${query}`)
                  .then((response) => response.json())
                  .then((data) => data.map(({ uid, iso }) => ({ value: uid, label: iso })))
                  , 250)
              }
            />
          </div>
          <div className="flex items-center w-5/12 space-x-2">
            <Input
              required
              size="sm"
              label="Price"
              type="number"
              value={rates.price}
              onChange={({ target: { value } }) => setRates((prev) => ({ ...prev, price: value }))}
            />

            <SelectAsyncInline
              disabled={!roles.includes('finance')}
              className="mt-12"
              size="sm"
              value={rates.priceCurrency}
              onChange={(value) => setRates((prev) => ({ ...prev, priceCurrency: value }))}
              options={[]}
              cacheOptions
              defaultOptions
              isClearable
              loadOptions={
                debounce((query) => fetch(`/rest/v1/currency?search[iso]=${query}`)
                  .then((response) => response.json())
                  .then((data) => data.map(({ uid, iso }) => ({ value: uid, label: iso })))
                  , 250)
              }
            />
          </div>

          <div className="flex w-2/12">
            <Input
              size="sm"
              label="Discount"
              type="number"
              value={rates.discount}
              onChange={({ target: { value } }) => setRates((prev) => ({ ...prev, discount: value }))}
            />
          </div>
        </div>

        <div>
          <label className="text-gray-700 text-sm font-medium">Deadline</label>

          <DateTime
            className="w-full"
            value={deadline}
            onChange={(value) => setDeadline(value)}
            inputProps={{className: "h-9 mt-2 text-center rounded-sm w-1/2 border-t-gray-300 border"}}
            closeOnTab
            closeOnSelect
          />
        </div>
        {!!fileSpecOptions?.length &&
          <Select
            required
            isMulti
            label="Product Info (select at least one option)"
            size="sm"
            value={fileSpecs}
            onChange={(value) => setFileSpecs(value)}
            options={fileSpecOptions?.map(({ uid, name }) => ({ value: uid, label: name }))}
          />
        }

        {reAssign && 
          <TextArea
            label="Internal note"
            description="This message will only be viewable for coordinators"
            rows={4}
            value={internalMsg}
            onChange={({ target: { value } }) => setInternalMsg(value)}
          />
        }
        <TextArea
          required
          label="Notification message"
          description="This message will be sent to the supplier"
          rows={10}
          value={brief}
          onChange={({ target: { value } }) => setBrief(value)}
        />

        <Button onClick={onSubmit} disabled={disabled}>
          { loading ? 'Loading' : 'Assign' }
        </Button>
      </div>
    </Modal>
  )
})

function resolveLatestAssignedTask(order) {
  let latestAssignedTask = null

  for (const job of order.tasks) {
    for (const version of job.versions) {
      for (const task of version.tasks) {
        if (task?.supplier?.uid && parseInt(task.tstamp) > parseInt(latestAssignedTask?.tstamp || 0)) {
          latestAssignedTask = task
        }
      }
    }
  }

  return latestAssignedTask
}
